import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { openSecureMedia } from '@/lib/secureMedia';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ExternalLink, GraduationCap, FileCheck2, Award } from 'lucide-react';
import { useBranding } from '@/lib/branding';

const TABS = [
  ['training', 'Mandatory Training'], ['competencies', 'Competencies'], ['certificates', 'Certificates'],
  ['modules', 'Learning Modules'], ['policies', 'Read & Sign Policies'], ['records', 'Training Records'],
];

const STATUS_BADGE = (s) => (
  <Badge variant="outline" className={`text-[10px] ${s === 'EXPIRED' || s === 'OVERDUE' ? 'border-red-300 text-red-700' : s === 'DUE_SOON' ? 'border-amber-300 text-amber-700' : 'border-emerald-300 text-emerald-700'}`}>{s}</Badge>
);

function useGet(url, params) {
  const [state, set] = useState({ data: null, error: null, loading: true });
  const load = useCallback(async () => {
    try { const r = await api.get(url, { params }); set({ data: r.data.data ?? r.data, error: null, loading: false }); }
    catch (e) {
      const st = e?.response?.status;
      set({ data: null, error: st === 404 ? 'Learning is not enabled for your organisation yet.' : st === 403 ? 'You don\u2019t have access to this section.' : 'Failed to load', loading: false });
    }
    // eslint-disable-next-line
  }, [url, JSON.stringify(params)]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

function PoliciesTab() {
  const { data, error, loading, reload } = useGet('/policies/my/pending');
  const items = Array.isArray(data) ? data : (data?.items || []);
  const ack = async (p) => {
    try {
      await api.post(`/policies/${p.policyId || p.policy?.id}/acknowledge`, {});
      toast.success('Policy acknowledged');
      reload();
      window.dispatchEvent(new Event('staff-v2-learning-changed'));
    } catch (e) { toast.error(e?.response?.data?.detail?.error?.message || e?.response?.data?.detail || 'Unable to acknowledge'); }
  };
  if (loading) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>;
  if (error) return <div className="py-8 text-center text-sm text-muted-foreground">{error}</div>;
  if (items.length === 0) return <div className="py-8 text-center text-sm text-muted-foreground" data-testid="policies-empty"><FileCheck2 className="h-8 w-8 mx-auto mb-2 opacity-30" />No policies awaiting your acknowledgement.</div>;
  return items.map((p) => (
    <div key={p.id} className="py-3 border-b last:border-0 flex flex-wrap items-center gap-2" data-testid={`policy-${p.id}`}>
      <div className="flex-1 min-w-[200px]">
        <div className="text-sm font-medium">{p.policy?.title || p.title || 'Policy'}</div>
        <div className="text-xs text-muted-foreground">Version {p.policyVersion || '—'}{p.dueDate ? ` · due ${p.dueDate}` : ''}{p.policy?.policyNo ? ` · ${p.policy.policyNo}` : ''}</div>
      </div>
      <Button size="sm" className="h-7" onClick={() => ack(p)} data-testid={`policy-ack-${p.id}`}>Read &amp; acknowledge</Button>
    </div>
  ));
}

export default function LearningPage() {
  const { organisationName } = useBranding();
  const [tab, setTab] = useState('training');
  const training = useGet('/staff/learning/training');
  const certs = useGet('/staff/learning/certificates');
  const modules = useGet('/staff/learning/modules');
  const [recordSearch, setRecordSearch] = useState('');
  const records = useGet('/staff/learning/records', { search: recordSearch || undefined });

  const honest = (msg, testId) => <div className="py-8 text-center text-sm text-muted-foreground max-w-md mx-auto" data-testid={testId}>{msg}</div>;

  const content = {
    training: training.error ? honest(training.error, 'learning-disabled')
      : honest(training.data?.reason || 'Loading…', 'training-unavailable'),
    competencies: honest(`Competency assessments are not yet managed in ${organisationName}. Your assessor records them with HR — see Certificates and Training Records for current evidence.`, 'competencies-unavailable'),
    certificates: certs.loading ? honest('Loading…') : certs.error ? honest(certs.error) : (certs.data || []).length === 0
      ? honest('No certificates or qualifications on file. Contact HR to upload your documents.', 'certs-empty')
      : (certs.data || []).map((c, i) => (
        <div key={i} className="py-3 border-b last:border-0 flex flex-wrap items-center gap-2" data-testid={`cert-${i}`}>
          <Award className="h-4 w-4 text-primary" />
          <div className="flex-1 min-w-[180px]">
            <div className="text-sm font-medium">{c.name}</div>
            <div className="text-xs text-muted-foreground">{c.issuer ? `${c.issuer} · ` : ''}{c.issue_date ? `issued ${c.issue_date} · ` : ''}{c.expiry_date ? `expires ${c.expiry_date}` : 'no expiry'}</div>
          </div>
          {STATUS_BADGE(c.status)}
          {c.media_path && (
            <Button size="sm" variant="outline" className="h-7" data-testid={`cert-view-${i}`} onClick={async () => {
              try {
                await openSecureMedia(c.media_path);
              } catch { toast.error('Unable to open certificate'); }
            }}>View</Button>
          )}
        </div>
      )),
    modules: modules.loading ? honest('Loading…') : modules.data?.available ? (
      <div className="py-6 text-center space-y-3" data-testid="lms-available">
        <GraduationCap className="h-8 w-8 mx-auto text-primary" />
        <p className="text-sm text-muted-foreground">Your organisation&apos;s external learning platform is available. Completion records do not sync back automatically.</p>
        <Button asChild size="sm" data-testid="lms-launch"><a href={modules.data.external_lms_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4 mr-1.5" />Open learning platform</a></Button>
      </div>
    ) : honest(modules.data?.reason || modules.error || 'No external learning platform is configured.', 'lms-unavailable'),
    policies: <PoliciesTab />,
    records: (
      <div className="space-y-3">
        <Input placeholder="Search records…" value={recordSearch} onChange={(e) => setRecordSearch(e.target.value)} className="max-w-xs" data-testid="records-search" aria-label="Search training records" />
        {records.loading ? honest('Loading…') : records.error ? honest(records.error) : (records.data || []).length === 0
          ? honest('No training history yet.', 'records-empty')
          : (records.data || []).map((r, i) => (
            <div key={i} className="py-2.5 border-b last:border-0 flex items-center gap-2" data-testid={`record-${i}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-muted-foreground">{r.kind === 'POLICY_ACK' ? 'Policy acknowledgement' : 'Certificate'}{r.provider ? ` · ${r.provider}` : ''}{r.date ? ` · ${new Date(r.date).toLocaleDateString()}` : ''}{r.expiry_date ? ` · expires ${r.expiry_date}` : ''}</div>
              </div>
              {STATUS_BADGE(r.status)}
            </div>
          ))}
      </div>
    ),
  };

  return (
    <div className="space-y-4" data-testid="learning-page">
      <div className="flex flex-wrap gap-1.5">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium focus-visible:ring-2 focus-visible:ring-primary ${tab === k ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  aria-current={tab === k ? 'page' : undefined} data-testid={`learning-tab-${k}`}>{label}</button>
        ))}
      </div>
      <Card><CardContent className="py-3" data-testid="learning-content">{content[tab]}</CardContent></Card>
    </div>
  );
}
